import { useEditor, EditorContent } from '@tiptap/react';
import { useEffect, useRef, useState } from 'react';
import Heading from '@tiptap/extension-heading';
import LinkExt from '@tiptap/extension-link';
import Blockquote from '@tiptap/extension-blockquote';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Paragraph from '@tiptap/extension-paragraph';
import TextExt from '@tiptap/extension-text';
import Document from '@tiptap/extension-document';
import HardBreak from '@tiptap/extension-hard-break';
import History from '@tiptap/extension-history';
import Image from '@tiptap/extension-image';
import Youtube from '@tiptap/extension-youtube';
import { cfImageUrl } from '@viet-parenting/supabase/cf-images';
import { supabase } from '../lib/supabase';

const IMAGES_HASH = import.meta.env.VITE_CF_IMAGES_ACCOUNT_HASH;

interface Props {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

async function uploadInlineImage(file: File): Promise<string> {
  const { data, error } = await supabase.functions.invoke<{ id: string; uploadURL?: string }>(
    'cf-image-upload',
  );
  if (error || !data) throw error ?? new Error('upload-url failed');
  if (data.uploadURL) {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(data.uploadURL, { method: 'POST', body: fd });
    if (!res.ok) throw new Error(`cf upload ${res.status}`);
  }
  return cfImageUrl(data.id, IMAGES_HASH, 'public');
}

// Extract a YouTube video ID from any common share-URL shape. Returns null if
// no video ID is present. Uses URL parsing so the order/length of query
// params (si, fbclid, feature, t, …) doesn't matter.
function parseYoutubeId(input: string): string | null {
  const trimmed = input.trim();
  let u: URL;
  try {
    u = new URL(trimmed);
  } catch {
    return null;
  }
  const host = u.hostname.replace(/^www\.|^m\./, '');
  const idShape = /^[A-Za-z0-9_-]{6,20}$/;

  // youtu.be/<id>
  if (host === 'youtu.be') {
    const id = u.pathname.slice(1).split('/')[0] ?? '';
    return idShape.test(id) ? id : null;
  }

  if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
    // /watch?v=<id>  (regardless of other query params)
    if (u.pathname === '/watch') {
      const id = u.searchParams.get('v') ?? '';
      return idShape.test(id) ? id : null;
    }
    // /embed/<id>, /shorts/<id>, /live/<id>, /v/<id>
    const m = u.pathname.match(/^\/(?:embed|shorts|live|v)\/([A-Za-z0-9_-]{6,20})/);
    if (m && m[1]) return m[1];
  }

  return null;
}

export default function RichTextEditor({ value, onChange, placeholder }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      Document,
      Paragraph,
      TextExt,
      HardBreak,
      History,
      Heading.configure({ levels: [2] }),
      Blockquote,
      Bold,
      Italic,
      LinkExt.configure({
        openOnClick: false,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      Image.configure({
        HTMLAttributes: { loading: 'lazy' },
      }),
      Youtube.configure({
        controls: true,
        nocookie: true,
        width: 560,
        height: 315,
        HTMLAttributes: { loading: 'lazy' },
      }),
    ],
    content: value,
    editorProps: {
      attributes: {
        class: 'min-h-[180px] p-3 text-sm text-gray-800 focus:outline-none rich-content',
        'data-placeholder': placeholder ?? '',
      },
    },
    onUpdate({ editor: ed }) {
      onChange(ed.getHTML());
    },
  });

  useEffect(() => () => editor?.destroy(), [editor]);

  // Allow external resets (e.g. when switching from new to edit)
  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() !== value) editor.commands.setContent(value, { emitUpdate: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const setLink = () => {
    const url = window.prompt('URL:');
    if (!url) return;
    editor?.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const insertYoutube = () => {
    if (!editor) return;
    const url = window.prompt('YouTube URL (e.g. https://youtu.be/dQw4w9WgXcQ):');
    if (!url) return;
    const id = parseYoutubeId(url);
    if (!id) {
      window.alert(
        'Link YouTube không hợp lệ. Cần URL có ID video, ví dụ:\n' +
          '  https://youtu.be/<ID>\n' +
          '  https://www.youtube.com/watch?v=<ID>\n' +
          '  https://www.youtube.com/shorts/<ID>',
      );
      return;
    }
    editor
      .chain()
      .focus()
      .setYoutubeVideo({ src: `https://www.youtube-nocookie.com/embed/${id}` })
      .run();
  };

  const handleImageFile = async (file: File) => {
    if (!editor) return;
    setUploading(true);
    try {
      const url = await uploadInlineImage(file);
      editor.chain().focus().setImage({ src: url, alt: file.name }).run();
    } catch (err) {
      window.alert(`Tải ảnh lên thất bại: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg">
      {/* Sticks just under the app header (h-14) so the formatting buttons stay
          reachable while editing a long body. The wrapper must not clip its
          overflow, or the sticky toolbar would scroll away with the content —
          hence the explicit top corner radius here instead of `overflow-hidden`. */}
      <div className="sticky top-14 z-10 flex items-center gap-1 px-2 py-1.5 border-b border-gray-100 bg-gray-50 rounded-t-[7px]">
        {[
          {
            label: 'H2',
            action: () => editor?.chain().focus().toggleHeading({ level: 2 }).run(),
            active: editor?.isActive('heading', { level: 2 }) ?? false,
            disabled: false,
          },
          {
            label: 'B',
            action: () => editor?.chain().focus().toggleBold().run(),
            active: editor?.isActive('bold') ?? false,
            disabled: false,
          },
          {
            label: 'I',
            action: () => editor?.chain().focus().toggleItalic().run(),
            active: editor?.isActive('italic') ?? false,
            disabled: false,
          },
          {
            label: '❝',
            action: () => editor?.chain().focus().toggleBlockquote().run(),
            active: editor?.isActive('blockquote') ?? false,
            disabled: false,
          },
          { label: '🔗', action: setLink, active: editor?.isActive('link') ?? false, disabled: false },
          {
            label: uploading ? '⏳' : '🖼️',
            action: () => fileInputRef.current?.click(),
            active: false,
            disabled: uploading,
          },
          { label: '▶', action: insertYoutube, active: false, disabled: false },
        ].map(({ label, action, active, disabled }) => (
          <button
            key={label}
            type="button"
            onClick={action}
            disabled={disabled}
            className={`px-2.5 py-1 text-xs font-bold rounded transition-colors disabled:opacity-50 ${
              active ? 'bg-red-700 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      <EditorContent editor={editor} />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleImageFile(file);
          e.target.value = '';
        }}
      />
    </div>
  );
}
